import { NO_MEMORY_REGION_NAME } from './AssetMemoryTypes';
import type {
    AssetMemoryAnalysis,
    AssetMemoryDiagnostic,
    AssetMemoryReportAsset,
    AssetMemorySizeEstimate,
    MemoryRegionReport,
    ParsedTarget,
    ParsedTargetMemoryRegion,
    TargetMemoryReport
} from './AssetMemoryTypes';

export type ResolvedAssetMemorySizeEstimate = AssetMemorySizeEstimate & {
    sizeBytes: number;
};

export type TargetMemoryInternal = ParsedTarget & {
    memoriesByName: Map<string, ParsedTargetMemoryRegion>;
};

export type TargetReportInternal = TargetMemoryReport & {
    regionsByName: Map<string, MemoryRegionReport>;
    noMemoryRegion?: MemoryRegionReport;
};

/**
 * Adds lookup maps to parsed targets so resolver checks can stay simple.
 */
export const createTargetMemoryInternals = (parsedTargets: ParsedTarget[]): TargetMemoryInternal[] =>
    parsedTargets.map((target: ParsedTarget) => ({
        ...target,
        memoriesByName: new Map(target.memories.map((memory: ParsedTargetMemoryRegion) => [memory.name, memory]))
    }));

/**
 * Creates mutable target reports from parsed memory declarations before assets are accumulated.
 */
export const createTargetReportInternals = (targetInternals: TargetMemoryInternal[]): TargetReportInternal[] =>
    targetInternals.map((target: TargetMemoryInternal) => {
        const memoryRegions: MemoryRegionReport[] = target.memories.map((memory: ParsedTargetMemoryRegion) =>
            createMemoryRegionReport(target.name, memory)
        );

        return {
            targetName: target.name,
            memoryRegions,
            regionsByName: new Map(memoryRegions.map((region: MemoryRegionReport) => [region.name, region]))
        };
    });

/**
 * Creates the mutable report entry for one declared memory region before assets are accumulated.
 */
const createMemoryRegionReport = (targetName: string, memory: ParsedTargetMemoryRegion): MemoryRegionReport => ({
    targetName,
    name: memory.name,
    section: memory.section,
    bandwidthBytesPerSecond: memory.bandwidthBytesPerSecond,
    capacityBytes: memory.sizeBytes,
    usedBytes: 0,
    utilization: null,
    overflowBytes: 0,
    isOverflowing: false,
    canOverflow: true,
    isUnspecified: false,
    assets: []
});

/**
 * Creates the synthetic bucket for assets that do not declare a memory region.
 * It has no capacity, so overflow/utilization are intentionally disabled.
 */
const createNoMemoryRegionReport = (targetName: string): MemoryRegionReport => ({
    targetName,
    name: NO_MEMORY_REGION_NAME,
    bandwidthBytesPerSecond: null,
    capacityBytes: null,
    usedBytes: 0,
    utilization: null,
    overflowBytes: 0,
    isOverflowing: false,
    canOverflow: false,
    isUnspecified: true,
    assets: []
});

/**
 * Lazily attaches the synthetic no-memory bucket to a target report.
 * Targets without unassigned assets avoid carrying an empty extra region.
 */
export const getNoMemoryRegion = (targetReport: TargetReportInternal): MemoryRegionReport => {
    if (!targetReport.noMemoryRegion) {
        targetReport.noMemoryRegion = createNoMemoryRegionReport(targetReport.targetName);
        targetReport.memoryRegions.push(targetReport.noMemoryRegion);
        targetReport.regionsByName.set(NO_MEMORY_REGION_NAME, targetReport.noMemoryRegion);
    }

    return targetReport.noMemoryRegion;
};

/**
 * Converts resolver output into the internal shape used by report rows.
 * Missing or unknown sizes become zero bytes for totals, while status/note preserve why the value is not exact.
 */
export const normalizeSizeEstimate = (
    value: number | AssetMemorySizeEstimate | null | undefined
): ResolvedAssetMemorySizeEstimate => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return {
            sizeBytes: Math.max(0, Math.round(value)),
            status: 'resolved'
        };
    }

    if (value && typeof value === 'object') {
        const sizeBytes: number =
            typeof value.sizeBytes === 'number' && Number.isFinite(value.sizeBytes)
                ? Math.max(0, Math.round(value.sizeBytes))
                : 0;

        return {
            sizeBytes,
            status: value.status,
            source: value.source,
            note: value.note
        };
    }

    return {
        sizeBytes: 0,
        status: 'unknown'
    };
};

/**
 * Finalizes mutable target reports by sorting assets, calculating totals, and deriving overflow/utilization.
 */
export const finishAnalysis = (
    reportInternals: TargetReportInternal[],
    parsedTargets: ParsedTarget[],
    diagnostics: AssetMemoryDiagnostic[]
): AssetMemoryAnalysis => {
    const targets: TargetMemoryReport[] = reportInternals.map((report: TargetReportInternal) => {
        for (const region of report.memoryRegions) {
            region.assets.sort((a: AssetMemoryReportAsset, b: AssetMemoryReportAsset) => b.sizeBytes - a.sizeBytes);
            region.usedBytes = region.assets.reduce(
                (sum: number, asset: AssetMemoryReportAsset) => sum + asset.sizeBytes,
                0
            );

            if (region.capacityBytes !== null) {
                region.utilization = region.capacityBytes > 0 ? (region.usedBytes / region.capacityBytes) * 100 : null;
                region.overflowBytes = Math.max(0, region.usedBytes - region.capacityBytes);
                region.isOverflowing = region.overflowBytes > 0;
            }
        }

        return {
            targetName: report.targetName,
            memoryRegions: report.memoryRegions
        };
    });

    return { targets, parsedTargets, diagnostics };
};
